import * as React from 'react';
import classNames from 'classnames';
import { assign as _assign, difference as _difference, includes as _includes } from 'lodash';
import { connect } from 'react-redux';
import { GlobalPrivateState } from 'src/api';
import { BuiltInPartsModel, BuiltInPartsDefaultModel, ConfigurationModel, LEFT_RIGHT, ConfigurationDefaultModel } from 'franke-trough-dependencies';
import { requestAPIActions } from 'loopback-react-request';
import { ElementPositions } from '../Utils';

import { FormattedMessage } from 'react-intl';
import MESSAGES from './Recesses.messages';
import { isConfigurationEditable } from 'src/redux/utils.redux';

export interface TapHoleProps {
  configuration: GlobalPrivateState['configuration'];
  dispatch: any;
}

export interface TapHoleState {
  tapHole: boolean;
  tapHolePositions: null | LEFT_RIGHT[];
}

export class TapHolePartial extends React.Component<TapHoleProps, TapHoleState> {
  constructor(props: TapHoleProps) {
    super(props);
    this.state = {
      tapHole: false,
      tapHolePositions: null,
    };

    this.handleChange = this.handleChange.bind(this);
    this.handlePositionChange = this.handlePositionChange.bind(this);
  }

  componentDidMount() {
    this.setState({
      tapHole: this.tapHole,
      tapHolePositions: this.tapHolePositions || [],
    });
  }

  componentDidUpdate(prevProps: TapHoleProps) {
    const configuration = this.configuration;
    const prevConfiguration = prevProps.configuration && prevProps.configuration.result;
    
    if ((configuration && configuration.updated_at) !== (prevConfiguration && prevConfiguration.updated_at)) {
      if (this.tapHole !== this.state.tapHole) {
        this.setState({
          tapHole: this.tapHole,
        });
      }

      if (this.tapHolePositions && JSON.stringify(this.tapHolePositions) !== JSON.stringify(this.state.tapHolePositions)) {
        this.setState({
          tapHolePositions: this.tapHolePositions,
        });
      }
    }
  }

  public render() {
    const { tapHole, tapHolePositions } = this.state;

    return (
      <div className="partial-tap-hole position-relative pb-4">
        {this.tapHoleDefaults && this.tapHoleDefaults.allValues && this.tapHoleDefaults.allValues.map((value, index) => {
          const key = `has_taphole_${index}`;
          const disabled = !(_includes(this.tapHoleDefaults && this.tapHoleDefaults.values, value))

          return (
            <div key={key} className="col-12">
              <label className={classNames({disabled: this.disabled || disabled})}>
                {!value ? (
                  <span>
                    <FormattedMessage {...MESSAGES.tapHoleWithout} />
                  </span>
                ) : (
                  <span>
                    <FormattedMessage {...MESSAGES.tapHoleWithoutDrilling} />
                  </span>
                )}
                <input 
                  checked={tapHole === value}
                  disabled={this.disabled || disabled}
                  onChange={this.handleChange}
                  name="tapHole"
                  type="radio"
                  value={value.toString()}
                />
                <span className="checkmark" />
              </label>
              {tapHole && value.toString() === 'true' && tapHolePositions && this.positionDefaults && (
                <ElementPositions 
                  defaults={this.positionDefaults}
                  disabled={this.disabled}
                  positions={tapHolePositions}
                  id={key}
                  positionChange={this.handlePositionChange}
                  selectedTrough={this.selectedTrough}
                />
              )}
            </div>
          );
        })}
      </div>
    );
  }

  private get configuration(): ConfigurationModel | undefined {
    return this.props.configuration && this.props.configuration.result;
  }

  private get defaults(): ConfigurationDefaultModel | undefined {
    return (this.configuration && this.configuration.defaults);
  }

  private get disabled(): boolean {
    return !isConfigurationEditable() || !this.configuration || !this.configuration.builtInParts;
  }

  private dispatchConfiguration(props: TapHoleState) {
    if (this.disabled) { return; }

    const builtInParts: BuiltInPartsModel = ((this.configuration as ConfigurationModel).builtInParts as BuiltInPartsModel);
    const data: BuiltInPartsModel = _assign({}, builtInParts, {
      tapHole: props.tapHole,
      tapHolePositions: props.tapHolePositions || null,
    });

    this.props.dispatch(requestAPIActions.load(
      'configuration', {
        url: `/Configurations/${this.configuration && this.configuration.id}`,
        options: {
          method: 'PATCH',
          data: {builtInParts: data}
        },
      },
      true,
    ));
  }

  private handleChange(event: React.SyntheticEvent<HTMLInputElement>) {
    const target = event.target as HTMLInputElement;
    const tapHole = target.value === 'true' && true || target.value === 'false' && false;
    const state = {
      tapHole,
      tapHolePositions: tapHole === true ? this.state.tapHolePositions : null,
    };
    this.setState(state);
    this.dispatchConfiguration(state);
  }

  private handlePositionChange(positions: LEFT_RIGHT[]) {
    const tapHolePositions = positions;
    const state = {
      tapHole: this.state.tapHole,
      tapHolePositions
    };
    this.setState(state);
    this.dispatchConfiguration(state);
  }

  private get positionDefaults(): BuiltInPartsDefaultModel['tapHolePositions'] | null | undefined {
    return this.defaults && this.defaults.builtInParts && this.defaults.builtInParts.tapHolePositions;
  }

  private get tapHole(): BuiltInPartsModel['tapHole'] {
    return this.configuration &&
      this.configuration.builtInParts &&
      this.configuration.builtInParts.tapHole ? this.configuration.builtInParts.tapHole : false;
  }

  private get tapHolePositions(): BuiltInPartsModel['tapHolePositions'] | null | undefined {
    return this.configuration &&
      this.configuration.builtInParts &&
      this.configuration.builtInParts.tapHolePositions;
  }

  private get tapHoleDefaults(): BuiltInPartsDefaultModel['tapHole'] | undefined {
    return this.defaults && this.defaults.builtInParts && this.defaults.builtInParts.tapHole;
  }

  private get selectedTrough(): ConfigurationModel['selectedTrough'] | undefined {
    return this.configuration &&
      this.configuration.selectedTrough;
  }
}

export const TapHole = connect((state: GlobalPrivateState) => ({
  configuration: state.configuration
}))(TapHolePartial);

export default TapHole;